import { useVersionCheck } from '../../hooks/useVersionCheck';
import clsx from 'clsx';
import MetronicCard from '@/app/components/layout/MetronicCard';

interface VersionUpdateNotificationProps {
  /** Position of the notification */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  /** Check interval in milliseconds. Default: 60000 (1 minute) */
  checkInterval?: number;
}

/**
 * Component that shows a notification when a new version of the app is available.
 * Includes a button to hard refresh the page and get the updated content.
 */
export function VersionUpdateNotification({
  position = 'bottom-right',
  checkInterval = 60000,
}: VersionUpdateNotificationProps) {
  const { hasNewVersion, currentVersion, newVersion, hardRefresh, dismiss } = useVersionCheck({
    checkInterval,
  });

  if (!hasNewVersion) {
    return null;
  }

  const positionClasses = {
    'top-right': 'top-0 end-0 mt-4 me-4',
    'top-left': 'top-0 start-0 mt-4 ms-4',
    'bottom-right': 'bottom-0 end-0 mb-4 me-4',
    'bottom-left': 'bottom-0 start-0 mb-4 ms-4',
  };

  return (
    <div
      className={clsx('position-fixed', positionClasses[position], 'animate__animated animate__fadeInUp')}
      style={{ zIndex: 9999, maxWidth: '380px' }}
    >
      <MetronicCard className="shadow-lg border-0">
        <div className="card-body p-4">
          <div className="d-flex align-items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10"
                style={{ width: '48px', height: '48px' }}
              >
                <i className="bi bi-arrow-repeat text-primary fs-2" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-grow-1">
              <h6 className="fw-bold mb-1 text-gray-900">Nova versão disponível!</h6>
              <p className="text-muted mb-3 fs-7">
                Uma nova versão do sistema está disponível.
                {newVersion ? (
                  <span className="d-block mt-1">
                    <span className="badge badge-light-secondary me-1">{currentVersion}</span>
                    <i className="bi bi-arrow-right mx-1 text-muted" />
                    <span className="badge badge-light-success">{newVersion}</span>
                  </span>
                ) : null}
              </p>

              {/* Actions */}
              <div className="d-flex gap-2">
                <button className="btn btn-primary btn-sm" type="button" onClick={hardRefresh}>
                  <i className="bi bi-arrow-clockwise me-1" />
                  Atualizar agora
                </button>
                <button className="btn btn-light-secondary btn-sm" type="button" onClick={dismiss}>
                  Mais tarde
                </button>
              </div>
            </div>

            {/* Close button */}
            <button
              aria-label="Fechar"
              className="btn btn-sm btn-icon btn-light-secondary"
              type="button"
              onClick={dismiss}
            >
              <i className="bi bi-x fs-4" />
            </button>
          </div>
        </div>
      </MetronicCard>
    </div>
  );
}
